/**
 * /privacy — public Privacy Policy page.
 *
 * Static, end-user-facing content page. Exists primarily so Google's OAuth
 * consent screen can validate a Privacy Policy URL at
 * `https://app.prod.nu-assist.com/privacy`. Public on purpose: registered
 * in `routing/routes.tsx` ABOVE the `WebShellLayout` route, mirroring how
 * `/login` and `/auth/callback` bypass `AuthGuard`.
 *
 * SINGLE SOURCE OF TRUTH for the policy TEXT is `./policy.md`. This file
 * only owns the page shell (logo, page heading) and the design-system
 * mapping for the rendered markdown elements. To update the policy
 * wording: edit `policy.md`, bump the "Last updated" date, open a PR.
 *
 * Pre-processing of `policy.md` before rendering:
 *   1. Strip HTML comments (`<!-- ... -->`). react-markdown without
 *      `rehype-raw` emits them as visible text; stripping them lets the
 *      markdown file carry editor instructions invisibly.
 *   2. Split on the first `---` horizontal rule into a metadata header
 *      (Last updated, Operator) rendered with muted text, and the policy
 *      body rendered with full prose styling.
 *
 * Security note: `react-markdown` does NOT enable raw-HTML rendering by
 * default. That default is load-bearing: it prevents an editor from
 * accidentally injecting unsafe markup into a public page. If you ever
 * add `rehype-raw`, you MUST also add `rehype-sanitize`.
 *
 * Styling: semantic HTML + Tailwind design-system tokens only
 * (`bg-bg`, `text-text`, `text-text-muted`, `text-accent`, …). No inline
 * `style={{}}` — see workspace memory `feedback_no_inline_styles.md` and
 * `@nu-assist/ui` token discipline.
 */
import type { ComponentProps } from "react";
import ReactMarkdown from "react-markdown";
import { Link } from "react-router-dom";

import policyMdRaw from "./policy.md?raw";

const policyMd = policyMdRaw.replace(/<!--[\s\S]*?-->/g, "").trim();
const [metaMdRaw, ...bodyParts] = policyMd.split(/\r?\n---\r?\n/);
const metaMd = (metaMdRaw ?? "").trim();
const bodyMd = bodyParts.join("\n---\n").trim();

type AnchorProps = ComponentProps<"a">;

function LinkComponent({ href, children }: AnchorProps) {
  const isExternal = href?.startsWith("http");
  return (
    <a
      href={href}
      className="text-accent underline"
      target={isExternal ? "_blank" : undefined}
      rel={isExternal ? "noopener noreferrer" : undefined}
    >
      {children}
    </a>
  );
}

function StrongComponent({ children }: ComponentProps<"strong">) {
  return <strong className="font-semibold text-text">{children}</strong>;
}

export function PrivacyPage() {
  return (
    <main
      data-testid="privacy-page"
      className="min-h-screen bg-bg text-text font-sans"
    >
      <div className="mx-auto max-w-3xl px-6 py-12">
        <header className="mb-10">
          <Link to="/" className="inline-block">
            <img
              src="/nu-logo-black.png"
              alt="nuSearch"
              className="h-10 w-auto bg-white p-2 rounded-md"
            />
          </Link>
          <h1 className="mt-8 text-h1 font-semibold text-text">
            Privacy Policy
          </h1>
          <div className="mt-3 text-body text-text-muted">
            <ReactMarkdown
              components={{
                p: ({ children }) => <p className="mb-1">{children}</p>,
                a: LinkComponent,
                strong: StrongComponent,
              }}
            >
              {metaMd}
            </ReactMarkdown>
          </div>
        </header>

        <article className="text-body text-text leading-relaxed">
          <ReactMarkdown
            components={{
              // Coerce any stray `# ...` in policy.md to render with the
              // body's h2 styling (and tag) — the page already owns the
              // sole real `<h1>` ("Privacy Policy") in the header above,
              // so an accidental top-level heading in the markdown must
              // NOT introduce a duplicate-h1 a11y violation or a bare,
              // unstyled heading.
              h1: ({ children }) => (
                <h2 className="mt-10 mb-3 text-h2 font-semibold text-text">
                  {children}
                </h2>
              ),
              h2: ({ children }) => (
                <h2 className="mt-10 mb-3 text-h2 font-semibold text-text">
                  {children}
                </h2>
              ),
              h3: ({ children }) => (
                <h3 className="mt-6 mb-2 text-h3 font-semibold text-text">
                  {children}
                </h3>
              ),
              p: ({ children }) => (
                <p className="mb-4 text-body text-text leading-relaxed">
                  {children}
                </p>
              ),
              ul: ({ children }) => (
                <ul className="mb-4 list-disc space-y-2 pl-6 text-body text-text leading-relaxed">
                  {children}
                </ul>
              ),
              li: ({ children }) => <li>{children}</li>,
              a: LinkComponent,
              strong: StrongComponent,
            }}
          >
            {bodyMd}
          </ReactMarkdown>
        </article>
      </div>
    </main>
  );
}
